<template>
  <div class='widget-W0160V0001'>
    <div class="wp-widget" lang="da">
      <div class="wp-inner" :class="{ 'wp-inner-full': isFullPlacement }">
        <div class="wp-week-bar">
          <div class="wp-loading-note" role="status">
            <transition name="wp-loading-note"><span v-if="isLoading && weekPlan" class="wp-loading-note-text">Henter ugeplan …</span>
            </transition>
          </div>
          <nav class="wp-week-nav" aria-label="Vælg uge" @mouseleave="closeWeekList">
            <div class="wp-week-nav-row">
              <button type="button" class="wp-week-nav-arrow" aria-label="Forrige uge" @click="stepWeek(-1)">
                <svg width="12" height="20" viewBox="0 0 6 10" fill="none" aria-hidden="true">
                  <path :d="icons.arrowLeft" fill="currentColor"></path>
                </svg>
              </button>
              <button
                type="button"
                class="wp-week-nav-label"
                :aria-expanded="isWeekListOpen ? 'true' : 'false'"
                @click="toggleWeekList"
                @keydown.esc="closeWeekList"
              >
                <span class="wp-week-nav-week">Uge {{ displayedWeekNumber }}<template
                  v-if="displayedWeekSuffix">, {{ displayedWeekSuffix }}
</template></span>
                {{ ' ' }}
                <span class="wp-week-nav-month">{{ displayedMonthText }}</span>
              </button>

              <transition name="wp-week-picker">
                <div v-if="isWeekListOpen" class="wp-week-picker" @pointerdown="isPointerChoosing = true"
                     @keydown="isPointerChoosing = false">
                  <button type="button" class="wp-week-nav-label wp-week-picker-spacer" disabled aria-hidden="true">
    <span class="wp-week-nav-week">Uge {{ displayedWeekNumber }}<template
      v-if="displayedWeekSuffix">, {{ displayedWeekSuffix }}</template></span>
                    <span class="wp-week-nav-month">{{ displayedMonthText }}</span>
                  </button>
                  <div class="wp-week-picker-body">
                    <div
                      class="wp-week-picker-list"
                      :class="{ 'wp-week-picker-list-snap': isWeekListSnapping }"
                      role="radiogroup"
                      aria-label="Uge"
                      @scroll.passive="stopWeekListSnapping"
                      @wheel.passive="stopWeekListSnapping"
                      @touchstart.passive="stopWeekListSnapping"
                      @pointerdown="stopWeekListSnapping"
                      @keydown="stopWeekListSnapping"
                    >
                      <label
                        v-for="option in weekOptions"
                        :key="option.isoDate"
                        class="wp-week-picker-item"
                        :class="{ 'wp-week-picker-item-selected': option.isoDate === displayedIsoDate }"
                      >
                        <input v-model="selectedWeekIsoDate" type="radio" class="wp-week-picker-radio"
                               :name="weekRadioName"
                               :value="option.isoDate">
                        <span class="wp-week-picker-week">
                      Uge {{ option.weekNumber }}
                      <span v-if="weekSuffix(option)" class="wp-week-picker-suffix">{{ weekSuffix(option) }}</span>
                    </span>
                        {{ ' ' }}
                        <span class="wp-week-picker-range">{{ option.rangeText }}</span>
                      </label>
                    </div>
                    <div class="wp-week-picker-footer">
                      <button type="button" class="wp-week-picker-today" @click="goToCurrentWeek">Til denne uge</button>
                    </div>
                  </div>
                </div>
              </transition>

              <button type="button" class="wp-week-nav-arrow" aria-label="Næste uge" @click="stepWeek(1)">
                <svg width="12" height="20" viewBox="0 0 6 10" fill="none" aria-hidden="true">
                  <path :d="icons.arrowRight" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          </nav>
        </div>

        <div v-if="isLoading && !weekPlan" class="wp-status" role="status">Henter ugeplan …</div>

        <div v-else-if="hasLoadError" class="wp-status" role="status" @focusin="closeWeekList">
          Ugeplanen kunne ikke hentes.
          <button type="button" class="wp-status-retry" @click="loadWeekPlan">Prøv igen</button>
        </div>

        <div
          v-else-if="weekPlan"
          class="wp-content"
          :class="{ 'wp-content-loading': isLoading }"
          :aria-busy="isLoading ? 'true' : null"
          @focusin="closeWeekList"
        >
          <section
            class="wp-noticeboard"
            :class="{ 'wp-noticeboard-closed': isNoticeBoardOpen === false }"
          >
            <div class="wp-card-header">
              <h2 class="wp-noticeboard-title">Opslagstavle</h2>
              <button
                type="button"
                class="wp-card-toggle"
                :aria-expanded="isNoticeBoardOpen ? 'true' : 'false'"
                :aria-label="isNoticeBoardOpen ? 'Fold opslagstavlen sammen' : 'Fold opslagstavlen ud'"
                @click="isNoticeBoardOpen = !isNoticeBoardOpen"
              >
                <svg class="wp-chevron" :class="{ 'wp-chevron-closed': isNoticeBoardOpen === false }" width="24"
                     height="24"
                     viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="icons.chevron" fill="#404040"></path>
                </svg>
              </button>
            </div>
            <div class="wp-drawer" :class="{ 'wp-drawer-open': isNoticeBoardOpen }">
              <div class="wp-drawer-body">
                <p v-if="childrenWithNoticeMessages.length === 0" class="wp-empty-text">Ingen nye beskeder</p>
                <div v-for="child in childrenWithNoticeMessages" :key="child.uniLoginId" class="wp-child-group">
                  <h3 v-if="visibleChildren.length > 1" class="wp-child-heading">
                    {{ childHeading(child) }}
                    <span v-if="showSchoolNames && child.schoolName" class="wp-child-school">{{
                        child.schoolName
                      }}</span>
                  </h3>
                  <div v-for="message in child.noticeBoardMessages" :key="message.id" class="wp-notice">
                    <p v-for="(line, lineIndex) in message.lines" :key="lineIndex" :class="line.classes"
                       :style="line.style"><span
                      v-if="line.prefix" class="wp-line-prefix">{{ line.prefix }}</span><img
                      v-if="line.kind === 'image'"
                      class="wp-image" :src="line.image"
                      :alt="line.alt"><span v-else
                                            class="wp-line-text"><component
                      :is="segment.link ? 'a' : 'span'" v-for="(segment, segmentIndex) in line.segments"
                      :key="segmentIndex"
                      :class="segment.isFile ? 'wp-file' : null" :style="segment.style" :href="segment.link || null"
                      :target="segment.link ? '_blank' : null" :rel="segment.link ? 'noopener' : null"><svg
                      v-if="segment.isFile"
                      class="wp-file-icon"
                      width="16" height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"><path
                      :d="icons.paperclip" fill="currentColor"></path></svg>{{ segment.text }}</component></span></p>
                    <a
                      v-for="attachment in message.attachments"
                      :key="attachment.id"
                      class="wp-attachment"
                      :href="attachment.url"
                      target="_blank"
                      rel="noopener"
                    >
                      <svg class="wp-attachment-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                           aria-hidden="true">
                        <path :d="icons.paperclip" fill="#404040"></path>
                      </svg>
                      {{ attachment.fileName }}</a>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <div class="wp-cal">
            <section
              v-for="day in weekDays"
              :key="day.isoDate"
              class="wp-day"
              :class="{
              'wp-day-today': day.isoDate === todayIsoDate,
              'wp-day-closed': !day.isOpen,
            }"
            >
              <div class="wp-card-header">
                <h2 class="wp-day-weekday">{{ day.weekdayLabel }}</h2>
                <span class="wp-day-date">{{ day.dayNumber }}. {{ day.monthLabel }}</span>
                <span v-if="day.isoDate === todayIsoDate" class="wp-today-tag">I dag</span>
                <button
                  type="button"
                  class="wp-card-toggle"
                  :aria-expanded="day.isOpen ? 'true' : 'false'"
                  :aria-label="dayToggleLabel(day)"
                  @click="toggleDay(day)"
                >
                  <svg class="wp-chevron" :class="{ 'wp-chevron-closed': !day.isOpen }" width="24" height="24"
                       viewBox="0 0 24 24"
                       fill="none" aria-hidden="true">
                    <path :d="icons.chevron" fill="#404040"></path>
                  </svg>
                </button>
              </div>

              <div class="wp-drawer" :class="{ 'wp-drawer-open': day.isOpen }">
                <div class="wp-drawer-body">
                  <div v-for="child in visibleChildren" :key="child.uniLoginId" class="wp-child-group">
                    <h3 v-if="visibleChildren.length > 1" class="wp-child-heading">
                      {{ childHeading(child) }}
                      <span v-if="showSchoolNames && child.schoolName" class="wp-child-school">{{
                          child.schoolName
                        }}</span>
                    </h3>

                    <p v-if="child.itemsByDay[day.weekdayIndex].length === 0" class="wp-empty-text">Intet nyt</p>

                    <div v-for="item in child.itemsByDay[day.weekdayIndex]" :key="item.id" class="wp-card-slot">
                      <div v-if="item.kind === 'event'" class="wp-event-card">
                        <p class="wp-event-title">{{ item.title }}</p>
                        <p v-for="(line, lineIndex) in item.lines" :key="lineIndex" :class="line.classes"
                           :style="line.style">
                          <span v-if="line.prefix" class="wp-line-prefix">{{ line.prefix }}</span><img
                          v-if="line.kind === 'image'" class="wp-image" :src="line.image" :alt="line.alt"><span v-else
                                                                                                                class="wp-line-text"><component
                          :is="segment.link ? 'a' : 'span'" v-for="(segment, segmentIndex) in line.segments"
                          :key="segmentIndex"
                          :class="segment.isFile ? 'wp-file' : null" :style="segment.style" :href="segment.link || null"
                          :target="segment.link ? '_blank' : null" :rel="segment.link ? 'noopener' : null"><svg
                          v-if="segment.isFile" class="wp-file-icon" width="16" height="16" viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"><path :d="icons.paperclip" fill="currentColor"></path></svg>{{
                            segment.text
                          }}</component></span></p>
                        <a
                          v-for="attachment in item.attachments"
                          :key="attachment.id"
                          class="wp-attachment"
                          :href="attachment.url"
                          target="_blank"
                          rel="noopener"
                        >
                          <svg class="wp-attachment-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                               aria-hidden="true">
                            <path :d="icons.paperclip" fill="#404040"></path>
                          </svg>
                          {{ attachment.fileName }}</a>
                      </div>

                      <div
                        v-else-if="item.kind === 'assignment'"
                        class="wp-assignment-card"
                        :style="{
                        borderColor: assignmentStatuses[item.status].color,
                        backgroundColor: assignmentStatuses[item.status].background,
                      }"
                      >
                        <div class="wp-assignment-content">
                          <div class="wp-assignment-text">
                            <div class="wp-assignment-row">
                              <strong>{{ item.title }}</strong>
                              <span v-if="item.dueTimeText">{{ item.dueTimeText }}</span>
                            </div>
                          </div>
                        </div>
                        <div
                          class="wp-assignment-status"
                          :style="{
                          backgroundColor: assignmentStatuses[item.status].bandColor,
                          borderColor: assignmentStatuses[item.status].color,
                          color: assignmentStatuses[item.status].textColor,
                        }"
                        >
                          <svg v-if="isDelivered(item.status)" width="20" height="20" viewBox="0 0 20 20" fill="none"
                               aria-hidden="true">
                            <path :d="icons.checkmark" fill="currentColor"></path>
                          </svg>
                          <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path :d="icons.upload" fill="currentColor"></path>
                          </svg>
                          <div class="wp-assignment-status-text">
                            <div v-if="item.deliveredAtText">{{ assignmentDeliveredAtText(item) }}</div>
                            <strong>{{ assignmentStatuses[item.status].label }}</strong>
                          </div>
                        </div>
                      </div>

                      <div v-else class="wp-lesson-card">
                        <span class="wp-lesson-bar" :style="{ backgroundColor: item.subjectColor }"
                              aria-hidden="true"></span>
                        <div class="wp-lesson-body">
                          <span class="wp-lesson-subject">{{ item.subjectName }}</span>
                          <div class="wp-lesson-description">
                            <p v-for="(line, lineIndex) in item.lines" :key="lineIndex" :class="line.classes"
                               :style="line.style">
                              <span v-if="line.prefix" class="wp-line-prefix">{{ line.prefix }}</span><img
                              v-if="line.kind === 'image'" class="wp-image" :src="line.image" :alt="line.alt"><span
                              v-else
                              class="wp-line-text"><component
                              :is="segment.link ? 'a' : 'span'" v-for="(segment, segmentIndex) in line.segments"
                              :key="segmentIndex" :class="segment.isFile ? 'wp-file' : null" :style="segment.style"
                              :href="segment.link || null" :target="segment.link ? '_blank' : null"
                              :rel="segment.link ? 'noopener' : null"><svg v-if="segment.isFile" class="wp-file-icon"
                                                                           width="16"
                                                                           height="16" viewBox="0 0 24 24" fill="none"
                                                                           aria-hidden="true"><path :d="icons.paperclip"
                                                                                                    fill="currentColor"></path></svg>{{
                                segment.text
                              }}</component></span></p>
                          </div>
                          <a
                            v-for="attachment in item.attachments"
                            :key="attachment.id"
                            class="wp-attachment"
                            :href="attachment.url"
                            target="_blank"
                            rel="noopener"
                          >
                            <svg class="wp-attachment-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                 aria-hidden="true">
                              <path :d="icons.paperclip" fill="#404040"></path>
                            </svg>
                            {{ attachment.fileName }}</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const uriToWeekplanApi = 'https://aulawidgetapi.nordiccloud.dk/api/weekplan/v1';

const millisecondsPerDay = 24 * 60 * 60 * 1000;

const addDays = (date, dayCount) => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() + dayCount);
  return result;
};

const mondayOfDate = (date) => addDays(date, -((date.getDay() + 6) % 7));

const isWeekendDay = (date) => date.getDay() === 0 || date.getDay() === 6;

const defaultDisplayedMonday = () => {
  const today = new Date();
  return addDays(mondayOfDate(today), isWeekendDay(today) ? 7 : 0);
};

const isoWeekNumberForDate = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  target.setUTCDate(target.getUTCDate() - ((target.getUTCDay() + 6) % 7) + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((target - firstThursday) / (7 * millisecondsPerDay));
};

const mondayOfIsoWeek = (isoYear, isoWeek) =>
  addDays(mondayOfDate(new Date(isoYear, 0, 4)), (isoWeek - 1) * 7);

const capitalizeFirst = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const toIsoDateString = (date) =>
  date.getFullYear() +
  '-' +
  String(date.getMonth() + 1).padStart(2, '0') +
  '-' +
  String(date.getDate()).padStart(2, '0');

const shortMonthName = (date) => date.toLocaleDateString('da-DK', {month: 'short'}).replace(/\.$/, '');
const longMonthName = (date) => date.toLocaleDateString('da-DK', {month: 'long'});

const weekRangeTextForMonday = (monday, monthName) => {
  const sunday = addDays(monday, 6);
  if (monday.getMonth() === sunday.getMonth()) {
    return monday.getDate() + '. til ' + sunday.getDate() + '. ' + monthName(sunday);
  }
  return monday.getDate() + '. ' + monthName(monday) + ' til ' + sunday.getDate() + '. ' + monthName(sunday);
};

const monthLineTextForMonday = (monday, monthName, separator) => {
  const sunday = addDays(monday, 6);
  const mondayYear = monday.getFullYear();
  const sundayYear = sunday.getFullYear();
  if (monday.getMonth() === sunday.getMonth()) {
    return capitalizeFirst(longMonthName(monday)) + ' ' + mondayYear;
  }
  if (mondayYear === sundayYear) {
    return capitalizeFirst(monthName(monday)) + separator + monthName(sunday) + ' ' + mondayYear;
  }
  return capitalizeFirst(monthName(monday)) + ' ' + mondayYear + separator + monthName(sunday) + ' ' + sundayYear;
};

const kindOrder = {event: 0, assignment: 1, lesson: 2};

const isHexColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value || ''));
const isHttpsLink = (value) => /^https:\/\/\S+$/i.test(String(value || ''));

const hasMark = (marks, type) => marks.some((mark) => mark && mark.type === type);

const markAttr = (marks, type, name) => {
  const mark = marks.filter((candidate) => candidate && candidate.type === type)[0];
  return mark && mark.attrs ? mark.attrs[name] : undefined;
};

const segmentFromTextNode = (node) => {
  const marks = Array.isArray(node.marks) ? node.marks : [];
  const style = {};
  if (hasMark(marks, 'bold')) style.fontWeight = '700';
  if (hasMark(marks, 'italic')) style.fontStyle = 'italic';
  if (hasMark(marks, 'underline')) style.textDecoration = 'underline';
  const color = markAttr(marks, 'textStyle', 'color');
  if (isHexColor(color)) style.color = color;
  const highlight = markAttr(marks, 'highlight', 'color');
  if (isHexColor(highlight)) style.backgroundColor = highlight;
  const size = parseInt(markAttr(marks, 'textStyle', 'fontSize'), 10);
  if (size >= 12 && size <= 250) style.fontSize = size + 'px';
  const link = markAttr(marks, 'link', 'href');
  return {text: String(node.text || ''), style, link: isHttpsLink(link) ? link : ''};
};

const segmentFromAttachmentNode = (node) => {
  const attrs = node.attrs || {};
  return {text: String(attrs.fileName || ''), style: {}, link: '', isFile: true, attachmentId: String(attrs.id || '')};
};

const segmentsFromInline = (nodes) =>
  (Array.isArray(nodes) ? nodes : [])
    .filter((node) => node && (node.type === 'text' || node.type === 'hardBreak' || node.type === 'attachment'))
    .map((node) => {
      if (node.type === 'hardBreak') return {text: '\n', style: {}, link: ''};
      if (node.type === 'attachment') return segmentFromAttachmentNode(node);
      return segmentFromTextNode(node);
    });

const alignFromNode = (node) => (node.attrs && (node.attrs.textAlign === 'center' || node.attrs.textAlign === 'right') ? node.attrs.textAlign : '');

const isTextBlock = (node) => Boolean(node) && (node.type === 'paragraph' || node.type === 'heading');

const isList = (node) => Boolean(node) && (node.type === 'bulletList' || node.type === 'orderedList');

const lineClasses = (kind, align) => ['wp-line', 'wp-line-' + kind, align ? 'wp-align-' + align : ''];

const lineIndent = (depth) => (depth ? {marginLeft: depth * 24 + 'px'} : null);

const lineFromTextBlock = (node, kind, prefix, depth) => ({
  kind,
  prefix,
  classes: lineClasses(kind, alignFromNode(node)),
  style: lineIndent(depth),
  segments: segmentsFromInline(node.content),
});

const lineFromImage = (node) => {
  const attrs = node.attrs || {};
  return isHttpsLink(attrs.src) ? {
    kind: 'image',
    prefix: '',
    classes: lineClasses('image', ''),
    style: null,
    segments: [],
    image: attrs.src,
    alt: String(attrs.alt || '')
  } : null;
};

const linesFromList = (node, lines, depth) => {
  const ordered = node.type === 'orderedList';
  const start = Number(node.attrs && node.attrs.start) || 1;
  (Array.isArray(node.content) ? node.content : []).forEach((item, index) => {
    const prefix = ordered ? (start + index) + '.' : '•';
    (Array.isArray(item && item.content) ? item.content : []).forEach((child, childIndex) => {
      if (isTextBlock(child)) lines.push(lineFromTextBlock(child, 'item', childIndex === 0 ? prefix : '', depth));
      else if (isList(child)) linesFromList(child, lines, depth + 1);
    });
  });
};

const linesFromDoc = (doc) => {
  const lines = [];
  (Array.isArray(doc.content) ? doc.content : []).forEach((node) => {
    if (isTextBlock(node)) lines.push(lineFromTextBlock(node, 'paragraph', '', 0));
    else if (isList(node)) linesFromList(node, lines, 0);
    else if (node && node.type === 'image' && lineFromImage(node)) lines.push(lineFromImage(node));
  });
  return lines;
};

const docFromJson = (text) => {
  try {
    const parsed = JSON.parse(text);
    return parsed && parsed.type === 'doc' ? parsed : null;
  } catch {
    return null;
  }
};

const linesFromContent = (content) => {
  const doc = docFromJson(String(content || ''));
  return doc ? linesFromDoc(doc) : [];
};

const dateFromIsoDateString = (isoDate) => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ''));
  return parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : null;
};

const weekdayIndexForIsoDate = (monday, isoDate) => {
  const date = dateFromIsoDateString(isoDate);
  return date === null ? -1 : Math.round((date - monday) / millisecondsPerDay);
};

let widgetCount = 0;

const idListFromFilter = (rawSelection) => {
  const rawIds = Array.isArray(rawSelection) ? rawSelection : String(rawSelection || '').split(',');
  const ids = rawIds.map((rawId) => String(rawId).trim()).filter((id) => id.length > 0);
  return ids.filter((id, index) => ids.indexOf(id) === index);
};

const attachmentsOf = (event) => (Array.isArray(event.attachments) ? event.attachments : []);

const bodyFromApiEvent = (event) => {
  const lines = linesFromContent(event.content);
  const attachments = attachmentsOf(event);
  const placedIds = [];
  lines.forEach((line) =>
    line.segments.forEach((segment) => {
      if (!segment.isFile) return;
      const attachment = attachments.filter((candidate) => String(candidate.id) === segment.attachmentId)[0];
      if (!attachment) return;
      segment.text = String(attachment.fileName || segment.text);
      segment.link = isHttpsLink(attachment.url) ? attachment.url : '';
      placedIds.push(segment.attachmentId);
    })
  );
  return {lines, attachments: attachments.filter((attachment) => placedIds.indexOf(String(attachment.id)) === -1)};
};

const childFromApiUser = (user, institutionIds) => {
  const sections = Array.isArray(user.sections) ? user.sections : [];
  const section =
    sections.filter((candidate) => institutionIds.indexOf(String(candidate.institutionIdentifier)) > -1)[0] || sections[0] || {};
  return {
    uniLoginId: String(user.identifier || ''),
    firstName: user.firstName || user.displayName || '',
    className: section.sectionDisplayName || '',
    schoolName: section.institutionDisplayName || '',
    noticeBoardMessages: [],
    itemsByDay: [[], [], [], [], []],
  };
};

const noticeMessageFromApiEvent = (event) => {
  const body = bodyFromApiEvent(event);
  return {
    id: 'board-' + event.id,
    lines: body.lines,
    attachments: body.attachments,
  };
};

const itemFromApiEvent = (event, isoDate, weekdayIndex) => {
  const type = String(event.type || '').toLowerCase();
  const id = type + '-' + event.id + '-' + isoDate;
  const body = bodyFromApiEvent(event);
  if (type === 'subject') {
    return {
      id,
      weekdayIndex,
      kind: 'lesson',
      subjectName: event.label || event.identifier || '',
      subjectColor: event.color || '',
      lines: body.lines,
      attachments: body.attachments,
    };
  }
  return {
    id,
    weekdayIndex,
    kind: 'event',
    title: event.label || '',
    lines: body.lines,
    attachments: body.attachments,
  };
};

const itemsFromApiEvent = (event, monday) =>
  (Array.isArray(event.dates) ? event.dates : [])
    .map((isoDate) => ({isoDate, weekdayIndex: weekdayIndexForIsoDate(monday, isoDate)}))
    .filter((day) => day.weekdayIndex >= 0 && day.weekdayIndex <= 4)
    .map((day) => itemFromApiEvent(event, day.isoDate, day.weekdayIndex));

const weekPlanFromApiResponse = (response, monday, institutionIds) => {
  if (!response || !Array.isArray(response.users)) {
    throw new Error('Unexpected weekplan response');
  }
  const children = response.users.map((user) => childFromApiUser(user, institutionIds));
  const childrenByUniLoginId = {};
  children.forEach((child) => {
    childrenByUniLoginId[child.uniLoginId] = child;
  });
  const childrenForEvent = (event) => {
    const uniLoginIds = Array.isArray(event.users) && event.users.length > 0 ? event.users : Object.keys(childrenByUniLoginId);
    return uniLoginIds
      .map((uniLoginId) => childrenByUniLoginId[uniLoginId])
      .filter((child, index, list) => child && list.indexOf(child) === index);
  };
  (Array.isArray(response.board) ? response.board : []).forEach((event) => {
    const message = noticeMessageFromApiEvent(event);
    childrenForEvent(event).forEach((child) => child.noticeBoardMessages.push(message));
  });
  (Array.isArray(response.events) ? response.events : []).forEach((event) => {
    const items = itemsFromApiEvent(event, monday);
    childrenForEvent(event).forEach((child) => items.forEach((item) => child.itemsByDay[item.weekdayIndex].push(item)));
  });
  children.forEach((child) => child.itemsByDay.forEach((items) => items.sort((a, b) => kindOrder[a.kind] - kindOrder[b.kind])));
  return {monday, children};
};

const iconPaths = {
  arrowLeft:
    'M5.85349 9.22365C5.89994 9.26588 5.93678 9.31601 5.96192 9.37119C5.98706 9.42637 6 9.4855 6 9.54522C6 9.60495 5.98706 9.66408 5.96192 9.71926C5.93678 9.77444 5.89994 9.82457 5.85349 9.8668C5.80704 9.90903 5.75189 9.94253 5.6912 9.96538C5.63051 9.98824 5.56547 10 5.49978 10C5.43409 10 5.36904 9.98824 5.30835 9.96538C5.24766 9.94253 5.19252 9.90903 5.14607 9.8668L0.146627 5.32157C0.100144 5.27936 0.0632687 5.22923 0.0381093 5.17405C0.01295 5.11888 0 5.05973 0 5C0 4.94027 0.01295 4.88112 0.0381093 4.82594C0.0632687 4.77077 0.100144 4.72064 0.146627 4.67842L5.14607 0.133201C5.23988 0.0479136 5.36711 0 5.49978 0C5.63244 0 5.75968 0.0479136 5.85349 0.133201C5.9473 0.218487 6 0.334161 6 0.454775C6 0.575389 5.9473 0.691063 5.85349 0.77635L1.20713 5L5.85349 9.22365Z',
  arrowRight:
    'M5.85337 5.32157L0.853933 9.8668C0.807483 9.90903 0.752339 9.94253 0.691649 9.96538C0.630959 9.98824 0.565912 10 0.500222 10C0.434532 10 0.369485 9.98824 0.308795 9.96538C0.248106 9.94253 0.192961 9.90903 0.146511 9.8668C0.100061 9.82457 0.0632154 9.77444 0.0380769 9.71926C0.0129384 9.66408 0 9.60495 0 9.54522C0 9.4855 0.0129384 9.42637 0.0380769 9.37119C0.0632154 9.31601 0.100061 9.26588 0.146511 9.22365L4.79287 5L0.146511 0.77635C0.0527015 0.691063 0 0.575389 0 0.454775C0 0.334161 0.0527015 0.218487 0.146511 0.133201C0.240321 0.0479136 0.367555 0 0.500222 0C0.632889 0 0.760123 0.0479136 0.853933 0.133201L5.85337 4.67842C5.89986 4.72064 5.93673 4.77077 5.96189 4.82594C5.98705 4.88112 6 4.94027 6 5C6 5.05973 5.98705 5.11888 5.96189 5.17405C5.93673 5.22923 5.89986 5.27936 5.85337 5.32157Z',
  chevron: 'M7.41 15.41L12 10.83L16.59 15.41L18 14L12 8L6 14L7.41 15.41Z',
  checkmark: 'M17.5 5.8332L7.5 15.8332L2.91667 11.2499L4.09167 10.0749L7.5 13.4749L16.325 4.6582L17.5 5.8332Z',
  upload: 'M7.5 13.3333V8.33333H4.16667L10 2.5L15.8333 8.33333H12.5V13.3333H7.5ZM4.16667 16.6667V15H15.8333V16.6667H4.16667Z',
  paperclip:
    'M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z',
};

const assignmentStatuses = {
  standard: {
    label: 'Ikke afleveret endnu',
    color: '#7ab5fc',
    background: '#eff6ff',
    bandColor: '#dbeafe',
    textColor: '#172554'
  },
  deliveredOnTime: {
    label: 'Afleveret til tiden',
    color: '#68e796',
    background: '#f0fdf4',
    bandColor: '#dcfce7',
    textColor: '#14532d'
  },
  notDelivered: {
    label: 'Ikke afleveret',
    color: '#fc8b9a',
    background: '#fff1f2',
    bandColor: '#ffe4e6',
    textColor: '#4c0519'
  },
  deliveredLate: {
    label: 'Afleveret for sent',
    color: '#fcc939',
    background: '#fffbeb',
    bandColor: '#fef3c7',
    textColor: '#78350f'
  },
};

export default {
  name: 'Weekplan',

  data() {
    return {
      icons: iconPaths,
      assignmentStatuses,
      aulaToken: null,
      isLoading: true,
      hasLoadError: false,
      weekPlan: null,
      displayedMonday: defaultDisplayedMonday(),
      isNoticeBoardOpen: true,
      closedDayIsoDates: [],
      loadRequestId: 0,
      isWeekListOpen: false,
      isWeekListSnapping: false,
      isPointerChoosing: false,
      weekRadioName: 'wp-week-' + (widgetCount += 1),
    };
  },

  props: {
    axios: Function,
    getAulaToken: Function,
    sessionUUID: String,
    userProfile: String,
    assuranceLevel: Number,
    childFilter: Array,
    institutionFilter: Array,
    currentWeekNumber: String,
    isMobileApp: Boolean,
    placement: String,
    notifications: Array,
    deleteNotifications: Function,
  },

  mounted() {
    this.aulaToken = this.getAulaToken();
  },

  watch: {
    aulaToken() {
      if (this.aulaToken != undefined) {
        this.loadWeekPlan();
      }
    },

    childFilter(newSelection, oldSelection) {
      this.reloadIfSelectionChanged(newSelection, oldSelection);
    },

    institutionFilter(newSelection, oldSelection) {
      this.reloadIfSelectionChanged(newSelection, oldSelection);
    },

    currentWeekNumber: {
      immediate: true,
      handler() {
        const parts = /^(\d{4})-W(\d{1,2})$/.exec(this.currentWeekNumber || '');
        if (parts) {
          this.displayedMonday = mondayOfIsoWeek(Number(parts[1]), Number(parts[2]));
          if (this.aulaToken != undefined) this.loadWeekPlan();
        }
      },
    },
  },

  computed: {
    isFullPlacement() {
      return this.placement !== 'medium' && this.placement !== 'narrow';
    },

    displayedWeekNumber() {
      return isoWeekNumberForDate(this.displayedMonday);
    },

    displayedWeekSuffix() {
      if (this.isCurrentWeekDisplayed) return 'denne uge';
      if (this.isNextWeekDisplayed) return 'næste uge';
      return '';
    },

    todayIsoDate() {
      return toIsoDateString(new Date());
    },

    displayedIsoYear() {
      return addDays(this.displayedMonday, 3).getFullYear();
    },

    displayedIsoDate() {
      return toIsoDateString(this.displayedMonday);
    },

    selectedWeekIsoDate: {
      get() {
        return this.displayedIsoDate;
      },
      set(isoDate) {
        this.showWeek(dateFromIsoDateString(isoDate));
      },
    },

    currentMondayIsoDate() {
      return toIsoDateString(mondayOfDate(new Date()));
    },

    isCurrentWeekDisplayed() {
      return this.displayedIsoDate === this.currentMondayIsoDate;
    },

    weekendNextMondayIsoDate() {
      const today = new Date();
      if (isWeekendDay(today) === false) {
        return null;
      }
      return toIsoDateString(addDays(mondayOfDate(today), 7));
    },

    isNextWeekDisplayed() {
      return this.weekendNextMondayIsoDate !== null && this.displayedIsoDate === this.weekendNextMondayIsoDate;
    },

    monthName() {
      return this.isFullPlacement ? longMonthName : shortMonthName;
    },

    displayedMonthText() {
      return monthLineTextForMonday(this.displayedMonday, this.monthName, this.isFullPlacement ? ' til ' : '-');
    },

    weekOptions() {
      const options = [];
      let monday = mondayOfIsoWeek(this.displayedIsoYear, 1);
      while (addDays(monday, 3).getFullYear() === this.displayedIsoYear) {
        options.push({
          isoDate: toIsoDateString(monday),
          weekNumber: isoWeekNumberForDate(monday),
          rangeText: weekRangeTextForMonday(monday, this.monthName),
        });
        monday = addDays(monday, 7);
      }
      return options;
    },

    weekDays() {
      const monday = this.weekPlan ? this.weekPlan.monday : this.displayedMonday;
      const days = [];
      for (let weekdayIndex = 0; weekdayIndex < 5; weekdayIndex++) {
        const date = addDays(monday, weekdayIndex);
        const isoDate = toIsoDateString(date);
        days.push({
          weekdayIndex,
          isoDate,
          isOpen: this.closedDayIsoDates.indexOf(isoDate) === -1,
          dayNumber: String(date.getDate()),
          monthLabel: this.monthName(date),
          weekdayLabel: capitalizeFirst(date.toLocaleDateString('da-DK', {weekday: 'long'})),
        });
      }
      return days;
    },

    selectedUniLoginIds() {
      return idListFromFilter(this.childFilter);
    },

    selectedInstitutionIds() {
      return idListFromFilter(this.institutionFilter);
    },

    showSchoolNames() {
      const schoolNames = this.visibleChildren.map((child) => child.schoolName).filter(Boolean);
      return new Set(schoolNames).size > 1;
    },

    visibleChildren() {
      if (!this.weekPlan) {
        return [];
      }
      if (this.selectedUniLoginIds.length === 0) {
        return this.weekPlan.children;
      }
      return this.weekPlan.children.filter((child) => this.selectedUniLoginIds.indexOf(child.uniLoginId) > -1);
    },

    childrenWithNoticeMessages() {
      return this.visibleChildren.filter(
        (child) => Array.isArray(child.noticeBoardMessages) && child.noticeBoardMessages.length > 0,
      );
    },
  },

  methods: {
    async loadWeekPlan() {
      const requestId = this.loadRequestId + 1;
      this.loadRequestId = requestId;
      this.isLoading = true;
      this.hasLoadError = false;
      try {
        const weekPlan = await this.fetchWeekPlan();
        if (requestId !== this.loadRequestId) {
          return;
        }
        this.closedDayIsoDates = [];
        this.isNoticeBoardOpen = true;
        this.weekPlan = weekPlan;
        this.clearNotifications();
      } catch {
        if (requestId !== this.loadRequestId) {
          return;
        }
        this.hasLoadError = true;
      }
      this.isLoading = false;
    },

    async fetchWeekPlan() {
      const monday = this.displayedMonday;
      const queryParts = [];
      const addQueryPart = (name, value) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParts.push(name + '=' + encodeURIComponent(value));
        }
      };
      addQueryPart('userProfile', this.userProfile);
      addQueryPart('assuranceLevel', this.assuranceLevel);
      addQueryPart('childFilter', this.selectedUniLoginIds.join(','));
      addQueryPart('institutionFilter', this.selectedInstitutionIds.join(','));
      addQueryPart('isMobileApp', this.isMobileApp);
      addQueryPart('sessionUUID', this.sessionUUID);
      addQueryPart('datefrom', toIsoDateString(monday));
      addQueryPart('dateto', toIsoDateString(addDays(monday, 4)));
      const result = await this.axios.get(uriToWeekplanApi + '?' + queryParts.join('&'), {
        headers: {Authorization: 'Bearer ' + this.getAulaToken()},
      });
      return weekPlanFromApiResponse(result.data, monday, this.selectedInstitutionIds);
    },

    reloadIfSelectionChanged(newSelection, oldSelection) {
      const selectionKey = (selection) => idListFromFilter(selection).slice().sort().join(',');
      if (selectionKey(newSelection) !== selectionKey(oldSelection)) {
        this.loadWeekPlan();
      }
    },

    clearNotifications() {
      if (typeof this.deleteNotifications !== 'function' || !Array.isArray(this.notifications)) return;
      const ids = this.notifications.map((notification) => notification && notification.widgetNotificationId).filter(Boolean);
      if (ids.length) this.deleteNotifications(ids);
    },

    weekSuffix(option) {
      if (option.isoDate === this.currentMondayIsoDate) return 'denne uge';
      if (option.isoDate === this.weekendNextMondayIsoDate) return 'næste uge';
      return '';
    },

    childHeading(child) {
      return child.className ? child.firstName + ', ' + child.className : child.firstName;
    },

    isDelivered(status) {
      return status === 'deliveredOnTime' || status === 'deliveredLate';
    },

    assignmentDeliveredAtText(item) {
      const parts = item.deliveredAtText.split(' kl. ');
      if (parts.length === 2) {
        return parts[0].split(' ').join('\u00A0') + ' kl.\u00A0' + parts[1];
      }
      return item.deliveredAtText.split(' ').join('\u00A0');
    },

    dayToggleLabel(day) {
      return 'Fold ' + day.weekdayLabel.toLowerCase() + (day.isOpen ? ' sammen' : ' ud');
    },

    toggleDay(day) {
      if (this.closedDayIsoDates.indexOf(day.isoDate) > -1) {
        this.closedDayIsoDates = this.closedDayIsoDates.filter((isoDate) => isoDate !== day.isoDate);
      } else {
        this.closedDayIsoDates = this.closedDayIsoDates.concat(day.isoDate);
      }
    },

    toggleWeekList() {
      this.isWeekListOpen = this.isWeekListOpen === false;
      this.isWeekListSnapping = this.isWeekListOpen;
      this.isPointerChoosing = false;
    },

    stopWeekListSnapping() {
      this.isWeekListSnapping = false;
    },

    closeWeekList() {
      this.isWeekListOpen = false;
    },

    showWeek(monday) {
      this.displayedMonday = monday;
      this.loadWeekPlan();
      if (this.isPointerChoosing) this.closeWeekList();
    },

    goToCurrentWeek() {
      this.showWeek(mondayOfDate(new Date()));
      this.isWeekListSnapping = this.isWeekListOpen;
    },

    stepWeek(weekDelta) {
      this.displayedMonday = addDays(this.displayedMonday, weekDelta * 7);
      this.loadWeekPlan();
      this.isWeekListSnapping = this.isWeekListOpen;
    },
  },
};
</script>

<style lang="scss">
.widget-W0160V0001 {
  .wp-widget {
    padding: 16px 0 24px;
    font-family: Lato, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.43;
    color: #404040;
  }

  .wp-widget strong {
    font-weight: 700;
  }

  .wp-widget p, .wp-widget h2, .wp-widget h3 {
    margin: 0;
    padding: 0;
  }

  .wp-widget button:focus:not(:focus-visible) {
    outline: none;
  }

  .wp-inner {
    margin: 0 auto;
  }

  .wp-inner.wp-inner-full {
    max-width: 640px;
  }

  .wp-status {
    padding: 24px 0;
    text-align: center;
  }

  .wp-loading-note {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    text-align: center;
    pointer-events: none;
  }

  .wp-loading-note-text {
    display: inline-block;
    padding: 4px 12px;
    background-color: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 6px 12px -8px rgba(0, 0, 0, 0.25);
    font-size: 15px;
    color: #404040;
  }

  .wp-loading-note-enter-active {
    transition: opacity 0.2s ease 0.5s;
  }

  .wp-loading-note-enter {
    opacity: 0;
  }

  .wp-content {
    transition: opacity 0.15s ease;
  }

  .wp-content-loading {
    opacity: 0.5;
    pointer-events: none;
    transition: opacity 0.2s ease 0.5s;
  }

  .wp-status-retry {
    display: block;
    margin: 12px auto 0;
    padding: 8px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background-color: #ffffff;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    color: #374151;
    cursor: pointer;
  }

  .wp-week-bar,
  .wp-noticeboard,
  .wp-day {
    background-color: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
  }

  .wp-week-nav-arrow,
  .wp-week-nav-label,
  .wp-week-picker-today,
  .wp-card-toggle {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
  }

  .wp-week-bar {
    position: sticky;
    top: 0;
    z-index: 10;
    border-radius: 0 0 8px 8px;
    margin: -16px 0 24px;
    padding: 6px 8px;
    box-shadow: 0 6px 12px -8px rgba(0, 0, 0, 0.25);
  }

  .wp-week-nav {
    position: relative;
  }

  .wp-week-nav-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  }

  .wp-week-nav-arrow {
    width: 72px;
    flex: 0 1 72px;
    min-width: 40px;
    position: relative;
    z-index: 1;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: #4b5563;
  }

  .wp-week-nav-arrow svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .wp-week-nav-label {
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    max-width: calc(100% - 88px);
    font-size: 16px;
    font-weight: 700;
    color: #374151;
    padding: 4px 8px;
    border-radius: 4px;
    line-height: 1.5;
    white-space: nowrap;
  }

  .wp-week-nav-week {
    display: block;
    min-width: 128px;
  }

  .wp-week-nav-month {
    display: block;
    margin-top: 1px;
    white-space: normal;
    font-size: 15px;
    font-weight: 400;
    color: #4b5563;
  }

  .wp-week-picker {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, -8px);
    z-index: 0;
    width: calc(100% + 20px);
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    padding-bottom: 4px;
  }

  .wp-week-picker-spacer {
    display: block;
    visibility: hidden;
    max-width: calc(100% - 108px);
    margin-bottom: 20px;
  }

  .wp-week-picker-body {
    overflow: hidden;
    max-height: 440px;
    transition: max-height 0.25s ease;
  }

  .wp-week-picker-enter-active {
    transition: opacity 0.1s ease;
  }

  .wp-week-picker-leave-active {
    transition: opacity 0.12s ease 0.13s;
  }

  .wp-week-picker-enter,
  .wp-week-picker-leave-to {
    opacity: 0;
  }

  .wp-week-picker-enter .wp-week-picker-body,
  .wp-week-picker-leave-to .wp-week-picker-body {
    max-height: 0;
  }

  .wp-week-picker-list {
    box-shadow: 0 -1px 0 #f3f4f6;
    position: relative;
    max-height: 240px;
    overflow-x: hidden;
    overflow-y: auto;
    margin: 0;
    padding: 4px 8px 0;
    list-style: none;
  }

  .wp-week-picker-item {
    position: relative;
    width: 100%;
    margin: 0;
    color: inherit;
    font-weight: 400;
    text-transform: none;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px 24px;
    border: 2px solid transparent;
    background-clip: padding-box;
    padding: 7px 8px;
    border-radius: 4px;
    text-align: left;
    white-space: normal;
    line-height: 1.5;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
  }

  .wp-widget .wp-week-picker-item .wp-week-picker-radio {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
    scroll-margin: 8px 0;
  }

  .wp-week-picker-item:focus-within {
    box-shadow: 0 0 3px 1px #19425f;
  }

  .wp-week-picker-item-selected {
    background-color: #e5e7eb;
  }

  .wp-week-picker-item + .wp-week-picker-item {
    margin-top: 4px;
  }

  .wp-week-picker-list-snap {
    scroll-snap-type: y mandatory;
  }

  .wp-week-picker-list-snap .wp-week-picker-item-selected {
    scroll-snap-align: center;
  }

  .wp-week-picker-item:active:not(.wp-week-picker-item-selected) {
    background-color: #f3f4f6;
  }

  .wp-week-picker-week {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .wp-week-picker-suffix {
    display: block;
    font-weight: 400;
    color: #4b5563;
  }

  .wp-week-picker-range {
    margin-left: auto;
    font-size: 15px;
    color: #4b5563;
    text-align: right;
  }

  .wp-week-picker-footer {
    margin-top: 4px;
    border-top: 1px solid #f3f4f6;
    padding: 8px 8px 4px;
  }

  .wp-week-picker-today {
    width: 100%;
    font-size: 15px;
    font-weight: 700;
    color: #374151;
    padding: 10px 8px;
    border-radius: 4px;
    text-align: center;
  }

  .wp-drawer {
    display: grid;
    grid-template-rows: 0fr;
    visibility: hidden;
    transition: grid-template-rows 0.25s ease, visibility 0s linear 0.25s;
  }

  .wp-drawer-open {
    grid-template-rows: 1fr;
    visibility: visible;
    transition: grid-template-rows 0.25s ease, visibility 0s;
  }

  .wp-drawer-body {
    overflow: hidden;
    min-height: 0;
  }

  .wp-noticeboard {
    margin-bottom: 24px;
  }

  .wp-noticeboard-title {
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
    color: #404040;
  }

  .wp-notice {
    background-color: #ffffff;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 16px;
  }

  .wp-line-text {
    white-space: pre-line;
  }

  .wp-line-text:empty::before {
    content: '\00a0';
  }

  .wp-line-text a {
    color: #2563eb;
    text-decoration: underline;
  }

  .wp-line-text .wp-file {
    color: #4b5563;
    text-decoration: none;
  }

  .wp-line-text a.wp-file:hover {
    text-decoration: underline;
  }

  .wp-file-icon {
    width: 1.1em;
    height: 1.1em;
    margin-right: 3px;
    vertical-align: -0.2em;
  }

  .wp-line-item {
    display: flex;
    gap: 6px;
    padding-left: 4px;
  }

  .wp-line-prefix {
    flex-shrink: 0;
    min-width: 14px;
  }

  .wp-image {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .wp-align-center {
    text-align: center;
  }

  .wp-align-right {
    text-align: right;
  }

  .wp-attachment {
    display: flex;
    gap: 4px;
    align-items: center;
    width: fit-content;
    font-weight: 400;
    font-size: 15px;
    color: #4b5563;
    text-decoration: none;
  }

  .wp-attachment-icon {
    flex-shrink: 0;
  }

  .wp-attachment:hover {
    text-decoration: underline;
  }

  .wp-notice .wp-attachment,
  .wp-lesson-body .wp-attachment {
    margin-top: 12px;
  }

  .wp-lesson-body .wp-attachment + .wp-attachment {
    margin-top: 4px;
  }

  .wp-notice .wp-attachment-icon {
    margin-left: -6px;
  }

  .wp-chevron {
    flex-shrink: 0;
    transition: transform 0.15s ease-out;
  }

  .wp-chevron-closed {
    transform: rotate(180deg);
  }

  .wp-noticeboard,
  .wp-day {
    padding: 11px 12px;
  }

  .wp-day + .wp-day {
    margin-top: 16px;
  }

  .wp-card-header {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    align-items: baseline;
    line-height: 1.5;
    padding-right: 40px;
  }

  .wp-card-toggle {
    position: absolute;
    top: -11px;
    right: -12px;
    bottom: -11px;
    left: -12px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: 12px;
  }

  .wp-day-weekday {
    font-weight: 700;
    font-size: 16px;
    color: #000000;
    white-space: nowrap;
  }

  .wp-day-date {
    flex-grow: 1;
    font-weight: 700;
    font-size: 14px;
    color: #4d4d4d;
    white-space: nowrap;
  }

  .wp-drawer-body > :first-child {
    margin-top: 14px;
  }

  .wp-drawer-body > :last-child {
    margin-bottom: 13px;
  }

  .wp-day-closed,
  .wp-noticeboard-closed {
    background-color: #f3f4f6;
  }

  .wp-day-closed .wp-day-weekday,
  .wp-day-closed .wp-day-date,
  .wp-noticeboard-closed .wp-noticeboard-title {
    color: #4b5563;
  }

  .wp-day-closed .wp-chevron,
  .wp-noticeboard-closed .wp-chevron {
    opacity: 0.6;
  }

  .wp-day-today {
    border-color: #007788;
    background-color: #e6f1f3;
  }

  .wp-today-tag {
    background-color: #007788;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    padding: 5px 10px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .wp-card-header .wp-today-tag {
    align-self: center;
  }

  .wp-child-group + .wp-child-group {
    margin-top: 40px;
  }

  h3.wp-child-heading {
    font-weight: 700;
    font-size: 16px;
    color: #000000;
    margin: 0 0 14px;
  }

  .wp-child-school {
    display: inline-block;
    font-weight: 400;
    font-size: 15px;
    color: #4b5563;
  }

  .wp-card-slot + .wp-card-slot,
  .wp-notice + .wp-notice {
    margin-top: 16px;
  }

  .wp-empty-text {
    margin: 0;
    color: #4b5563;
  }

  .wp-event-card {
    color-scheme: only light;
    background-color: #fff1f2;
    border: 2px solid #fc8b9a;
    border-radius: 4px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .wp-event-card p {
    color: #374151;
    text-align: center;
  }

  .wp-event-card .wp-attachment {
    margin: 0 auto;
  }

  .wp-event-title {
    font-weight: 700;
    font-size: 16px;
    text-align: center;
    color: #1f2937;
  }

  .wp-assignment-card {
    border-radius: 4px;
    border: 2px solid;
    border-top-width: 8px;
    color-scheme: only light;
  }

  .wp-assignment-content {
    display: flex;
    gap: 4px;
    align-items: flex-start;
    padding: 8px;
  }

  .wp-assignment-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #374151;
  }

  .wp-assignment-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .wp-assignment-row span {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .wp-assignment-row strong {
    color: #1f2937;
  }

  .wp-assignment-status {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin: 0 8px 8px;
    border: 1px solid transparent;
    padding: 4px 8px;
    font-size: 15px;
    line-height: 1.4;
    border-radius: 4px;
  }

  .wp-assignment-status svg {
    flex-shrink: 0;
  }

  .wp-assignment-status-text {
    flex: 1;
    min-width: 0;
  }

  .wp-assignment-status-text strong {
    display: block;
  }

  .wp-lesson-card {
    display: flex;
    gap: 6px;
    align-items: stretch;
    background-color: #ffffff;
    border: 1px solid #ebebeb;
    border-radius: 4px;
  }

  .wp-lesson-bar {
    width: 8px;
    flex-shrink: 0;
    border-radius: 4px;
    color-scheme: only light;
  }

  .wp-lesson-body {
    flex: 1;
    min-width: 0;
    padding: 8px 8px 8px 0;
  }

  .wp-lesson-subject {
    display: block;
    width: fit-content;
    padding: 0 4px;
    font-weight: 700;
    font-size: 15px;
    color: #333333;
  }

  .wp-lesson-description {
    margin-top: 8px;
    padding-left: 4px;
    color: #404040;
  }

  .wp-week-nav-arrow:active,
  .wp-week-nav-label:active,
  .wp-week-picker-today:active,
  .wp-status-retry:active {
    background-color: #e5e7eb;
  }

  @media (min-width: 1200px) {
    .wp-widget .wp-inner.wp-inner-full {
      max-width: none;
    }

    .wp-inner-full .wp-week-nav-row {
      max-width: 336px;
      margin: 0 auto;
    }
    .wp-inner-full .wp-week-nav-label {
      font-size: 20px;
      padding: 4px 16px;
    }
    .wp-inner-full .wp-week-nav-week {
      min-width: 161px;
    }
    .wp-inner-full .wp-week-nav-arrow {
      width: 60px;
      flex: 0 1 60px;
    }
    .wp-inner-full .wp-week-picker {
      width: 360px;
    }
    .wp-inner-full .wp-week-picker-spacer {
      max-width: calc(100% - 112px);
    }
    .wp-inner-full .wp-week-picker-list {
      max-height: 288px;
    }
    .wp-inner-full .wp-week-picker-item {
      padding: 4px 8px;
    }

    .wp-inner-full .wp-cal {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 16px;
    }

    .wp-inner-full .wp-day {
      position: relative;
      padding: 24px 12px;
      min-height: 140px;
    }

    .wp-inner-full .wp-day + .wp-day {
      margin-top: 0;
    }
    .wp-inner-full .wp-day .wp-card-header {
      position: static;
      line-height: inherit;
      flex-direction: column;
      align-items: center;
      gap: 0;
      padding-right: 0;
      color: #4d4d4d;
      margin-bottom: 16px;
    }

    .wp-inner-full .wp-day-today .wp-card-header {
      color: #000000;
    }
    .wp-inner-full .wp-day-weekday {
      font-size: 22px;
      font-weight: 600;
      line-height: 1.2;
      color: inherit;
    }
    .wp-inner-full .wp-day-date {
      font-size: 15px;
      font-weight: 400;
      color: inherit;
    }
    .wp-inner-full .wp-day .wp-card-toggle {
      display: none;
    }

    .wp-inner-full .wp-day .wp-drawer {
      grid-template-rows: 1fr;
      visibility: visible;
    }

    .wp-inner-full .wp-day h3.wp-child-heading {
      font-size: 15px;
      margin-bottom: 12px;
    }
    .wp-inner-full .wp-day .wp-child-school {
      font-size: 13px;
    }
    .wp-inner-full .wp-day .wp-child-group + .wp-child-group {
      margin-top: 32px;
    }
    .wp-inner-full .wp-day .wp-drawer-body > :first-child {
      margin-top: 0;
    }
    .wp-inner-full .wp-day .wp-drawer-body > :last-child {
      margin-bottom: 0;
    }
    .wp-inner-full .wp-day .wp-today-tag {
      position: absolute;
      top: 8px;
      right: 8px;
    }

    .wp-inner-full .wp-day-closed:not(.wp-day-today) {
      background-color: #ffffff;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .wp-week-nav-arrow:hover,
    .wp-week-nav-label:hover,
    .wp-week-picker-today:hover,
    .wp-status-retry:hover {
      background-color: #e5e7eb;
    }
    .wp-week-picker-item:hover:not(.wp-week-picker-item-selected) {
      background-color: #f3f4f6;
    }
  }

  @media (max-height: 600px) {
    .wp-widget .wp-inner .wp-week-picker-list {
      max-height: 120px;
    }
  }

  @media (max-height: 400px) {
    .wp-widget .wp-inner .wp-week-bar {
      position: relative;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .wp-widget *,
    .wp-widget *::before,
    .wp-widget *::after {
      transition-duration: 0s !important;
    }
  }
}
</style>
